import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { useSelector } from 'react-redux';
import { Step, StepLabel, Stepper, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, LinearProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import _ from 'lodash-es';

import { ROUTE_DATASETS_MANAGEMENT } from 'pages/constants';
import useDataset from 'hooks/datasets/use-dataset';
import useDatasetConfigure from 'hooks/datasets/use-dataset-configure';
import useDatasetValidate from 'hooks/datasets/use-dataset-validate';
import PageContainer from 'components/elements/PageContainer';
import OpenInNewIcon from 'components/icons/OpenInNewIcon';
import LoaderSpinner from 'components/elements/LoaderSpinner';
import AlertCard from 'components/elements/AlertCard';
import InfoDrawer from 'components/elements/InfoDrawer';
import ValidationErrorDialog from 'components/elements/ValidationErrorDialog'
import ValidationProgressDialog from 'components/elements/ValidationProgressDialog';


import SegmentRulesConfigurationStep from './segment-rules-configuration-step';
import RunConfigurationStep from './run-configuration-step';
import TimeDataConfigurationStep from './time-data-configuration-step';
import FormWithInfiniteScroll from './feature-configuration';

const DatasetConfigure = () => {
  const { datasetId } = useParams();
  const { t } = useTranslation();
  const [dataset, setDataset] = useState(null);

  const { data, status } = useDataset({ datasetId: datasetId });

  useEffect(() => {
    if (data) {
      setDataset(data.data);
    }
  }, [data]);

  if (status === 'error') {
    return <AlertCard severity={'error'} height={400} message={'Something went wrong !'} />;
  }

  if (status === 'loading' || !dataset) {
    return <Grid container justifyContent='center' alignItems='center' sx={{ width: '100%', minHeight: 400 }}>
      <Grid item>
        <LoaderSpinner type='Bars' color='#175da8' secondaryColor={'#6abed5'} height={70} width={70} />
      </Grid>
    </Grid>;
  }

  return <PageContainer pageTitle={t('feature.datasets.configure.title', { name: dataset ? dataset.name : '' })}>
    <DatasetConfigurePage dataset={dataset} />
  </PageContainer>;
};

const DatasetConfigurePage = ({ dataset, containerHeight }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef();
  const { datasetId } = useParams();

  const [activeStep, setActiveStep] = useState(0);

  const isPDP = useSelector((state) => state.app.isPDP);
  const selectedDatasetId = useSelector((state) => state.app.selectedDatasetId);
  const [configs, setConfigs] = useState({
    pipelines: {
      trigger_trends: false,
      trigger_opportunities: false,
      recalculate_trends: false,
      recalculate_opportunities: false,
    },
  });
  const [originalConfigs, setOriginalConfigs] = useState({});
  const [configsUpdating, setConfigsUpdating] = useState(null);
  const [errors, setErrors] = useState(null);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [configForSave, setConfigForSave] = useState(null);
  const [recalculationOptions, setRecalculationOptions] = useState({
    recalculate_trends: false,
    recalculate_opportunities: false
  });

  const { mutateAsync, isLoading, status } = useDatasetConfigure();
  const {
    mutateAsync: validateConfigs,
    isLoading: isValidating,
    status: validationStatus,
    error: validationError
  } = useDatasetValidate();

  useEffect(() => {
    if (status === 'error') {
      setErrors(['Failed to save configurations']);
    } else {
      setErrors(null);
    }
  }, [status]);

  useEffect(() => {
    if (validationStatus === 'error') {
      const fallback = 'An unknown error occurred during validation.';
      let extractedMessages = [];

      try {
        const errorList = validationError?.error.map(item => item.message);

        if (Array.isArray(errorList)) {
          extractedMessages = errorList.map(err =>
            typeof err === 'string'
              ? err
              : err?.message || JSON.stringify(err)
          );
        } else if (typeof errorList === 'string' && errorList.trim()) {
          extractedMessages = [errorList];
        } else {
          extractedMessages = [fallback, JSON.stringify(validationError, null, 2)];
        }
      } catch (e) {
        extractedMessages = [fallback];
      }
      setErrors(extractedMessages);
      setValidationOpen(false);
    }
  }, [validationStatus, validationError]);

  const steps = [
    'Features',
    'Segment Rules',
    'Run',
    'Time Data',
  ];

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    const req = {
      ...configs,
      ...formRef.current.values,
    };
    setConfigs(req);
    setActiveStep(activeStep - 1);
  };

  const handleConfigure = () => {
    console.log('👉 handleConfigure called for step:', activeStep);
    if (activeStep===0){
      formRef.current?.submitForm();
    }else{
      console.log('👉 Calling handleSubmit on formRef');
      formRef.current.handleSubmit();
    }

  };

  const handleOriginalConfigs = (values) => {
    setOriginalConfigs({
      ...originalConfigs,
      ...values,
    });
  };

  const checkRecalculate = (req) => {
    let pipelines = {
      recalculate_trends: false,
      recalculate_opportunities: false,
    };

    // Feature change
    const originalFeatures = originalConfigs['features']?.map(d => d['trend_operation']) || [];
    const reqFeatures = req['features']?.map(d => d['trend_operation']) || [];

    const opportunitiesRecalculateParams = ['record_id', 'outcomes', 'levers', 'segment_features', 'mean_feature_for_opportunity', 'segment_rules'];

    for (const param of opportunitiesRecalculateParams) {
      if (!_.isEqual(originalConfigs[param], req[param])) {
        pipelines.recalculate_opportunities = true;
        break;
      }
    }

    if (req['mean_feature_for_opportunity'] === '' || (req['levers'] && req['levers'].length === 0)) {
      pipelines.recalculate_opportunities = false;
    }

    //trends
    const trendRecalculateParams = ['record_id', 'date_column', 'trend_diff_timeunit_lower', 'trend_diff_timeunit_upper', 'segment_features', 'time_units', 'segment_rules'];

    for (const param of trendRecalculateParams) {
      if (!_.isEqual(originalConfigs[param], req[param])) {
        pipelines.recalculate_trends = true;
        break;
      }
    }

    if (!_.isEqual(originalFeatures, reqFeatures)) {
      pipelines.recalculate_trends = true;
    }

    if (req['date_column'] === '') {
      pipelines.recalculate_trends = false;
    }

    return pipelines;
  };

  const saveConfigs = (req) => {
    const finalConfig = {
      ...req,
      pipelines: {
        ...req.pipelines,
        ...(req['date_column'] === '' ? { trigger_trends: false } : { trigger_trends: true }),
        trigger_trends: recalculationOptions.recalculate_trends,
        trigger_opportunities: recalculationOptions.recalculate_opportunities,
      },
    };

    mutateAsync({ datasetId, req: finalConfig }).then(() => {
      navigateToDatasetManagement();
    });
  };

  const handleSubmit = (values) => {
    console.log('✅ handleSubmit triggered with values:', values);
    const req = {
      ...configs,
      ...values,
      rca_processing_params: { 'training_terms': '', 'generate_columns': '', 'nan_threshold_to_drop_features': 0 },
    };
    setConfigs(req);
    if (activeStep < 3) {
      handleNext();
    } else {
      // Start validation before saving
      setValidationOpen(true);
      setValidationSuccess(false);
      setConfigForSave(req);

      validateConfigs({ datasetId, configs: req })
        .then(() => {
          setValidationSuccess(true);
          const recalculate = checkRecalculate(req);
          setRecalculationOptions(recalculate);
        })
        .catch((error) => {
          setValidationOpen(false);
        });
    }
  };

  const handleValidationClose = () => {
    setValidationOpen(false);
  };

  const handleSaveAfterValidation = () => {
    setValidationOpen(false);
    if (configForSave) {
      saveConfigs(configForSave);
    }
  };

  const handleRecalcOptionChange = (event) => {
    setRecalculationOptions({
      ...recalculationOptions,
      [event.target.name]: event.target.checked
    });
  };

  const onClose = (req) => {
    saveConfigs(req);
    setConfigsUpdating(null);

  };

  const onConfirm = (req) => {
    saveConfigs({
      ...req,
      pipelines: {
        ...req.pipelines,
        ...(req['date_column'] === ''? { trigger_trends: false }: { trigger_trends: true,}),
        trigger_opportunities: true,
      },
    });
    setConfigsUpdating(null);
  };

  const navigateToDatasetManagement = () => {
    navigate({
      pathname: ROUTE_DATASETS_MANAGEMENT,
      search: isPDP ? '' : `?${createSearchParams({ 'dataset': selectedDatasetId })}`,
    });
  };

  const handleErrorClose = () => {
    setErrors(null);
  };

  {console.log('📦 Rendering ErrorDialog with errors:', errors)}

  return <Grid container spacing={2}>
    <Grid container xs={12} item>
      <Grid item container spacing={1} xs={12} justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Grid
            item
            container
            alignItems='center'
            width='unset'
            sx={{ cursor: 'pointer' }}
            onClick={navigateToDatasetManagement}
          >
            <Grid item>
              <Typography>Manage Datasets</Typography>
            </Grid>
            <Grid item sx={{ pl: '0.3em', '&.MuiGrid-root': { display: 'flex' } }}>
              <OpenInNewIcon />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6} container justifyContent='flex-end' spacing={2} pt={0}>
          {activeStep > 0 && <Grid item pt={0}>
            <Tooltip title={t('Back')}>
              <Button onClick={handleBack}
                      disabled={isLoading || isValidating}
                      startIcon={<ArrowBackIcon />}
                      variant={'contained'}>
                {t('Back')}
              </Button>
            </Tooltip>
          </Grid>}
          {<Grid item pt={0}>
            <Tooltip title={t('Next')}>
              <Button onClick={handleConfigure}
                      disabled={isLoading || isValidating}
                      startIcon={activeStep < 3 ? <ArrowForwardIcon /> : <SaveIcon />}
                      variant={'contained'}>
                {t(activeStep < 3 ? 'Next' : 'Save')}
              </Button>
            </Tooltip>
          </Grid>}
        </Grid>
      </Grid>
    </Grid>

    <Grid item container xs={12} justifyContent={'center'} ml={'1em'}>
      <Grid container spacing={2} item sx={{ pt: '1em' }} xs={12}>

        <Paper sx={{ width: '100%', p: '2em' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={12}>
              {activeStep === 0 && <FormWithInfiniteScroll
                height={containerHeight - 220}
                formRef={formRef}
                onSubmit={handleSubmit}
                dataset={dataset}
                configs={configs}
                handleOriginalConfigs={handleOriginalConfigs}
              />}

              {activeStep === 1 && dataset &&
                <SegmentRulesConfigurationStep
                  height={containerHeight - 164}
                  formRef={formRef}
                  onSubmit={handleSubmit}
                  dataset={dataset}
                  configs={configs}
                  handleOriginalConfigs={handleOriginalConfigs}
                />}

              {activeStep === 2 &&
                <RunConfigurationStep
                  height={containerHeight - 164}
                  formRef={formRef}
                  onSubmit={handleSubmit}
                  dataset={dataset}
                  configs={configs}
                  handleOriginalConfigs={handleOriginalConfigs}
                />}

              {activeStep === 3 &&
                <TimeDataConfigurationStep
                  height={containerHeight - 164}
                  formRef={formRef}
                  onSubmit={handleSubmit}
                  dataset={dataset}
                  configs={configs}
                  handleOriginalConfigs={handleOriginalConfigs}
                />}
              <ValidationProgressDialog
                open={validationOpen}
                onClose={handleValidationClose}
                onSave={handleSaveAfterValidation}
                success={validationSuccess}
                isValidating={isValidating}
                recalculationOptions={recalculationOptions}
                onRecalcOptionChange={handleRecalcOptionChange}
                configForSave={configForSave}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
    <ValidationErrorDialog
      open={errors !== null}
      handleClose={handleErrorClose}
      message={errors?.length > 0 ? "Validation Errors" : "An error occurred"}
      errors={Array.isArray(errors) ? errors : [errors]}
    />
    <InfoDrawer width={'24em'}>
      <Typography>
        Welcome to the Configure dataset page.
      </Typography>
    </InfoDrawer>
  </Grid>;
};

export default DatasetConfigure;